import { Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { TraceRouteData } from "../../../models/trace-route-data";
import { AlertService } from "../../../services/alert.service";
import { TraceRouteDataUpdateType, TraceService } from "../../../services/trace.service";
import { TraceCreateConfirmationDialogComponent, TraceCreateConfirmationDialogData } from "./trace-create-confirmation-dialog/trace-create-confirmation-dialog.component";

export type UndoListItemType = {
  type: TraceRouteDataUpdateType,
  data: TraceRouteData
}
@Injectable({
  providedIn: 'root'
})
export class TraceCreateUndoService {
  private readonly _maxSize: number = 20;
  private _undoList: UndoListItemType[];
  private _confirmationDialog: MatDialogRef<TraceCreateConfirmationDialogComponent, { data: TraceCreateConfirmationDialogData }>;
  private _undoListForDataRef: string;
  public canUndoLastChange: WritableSignal<boolean> = signal(false);

  private _redoList: UndoListItemType[];
  public canRedoLastChange: WritableSignal<boolean> = signal(false);

  constructor(
    private _traceService: TraceService,
    private _translateService: TranslateService,
    private _dialog: MatDialog,
    private _alertService: AlertService,
  ) {
    this._undoList = [];
    this._confirmationDialog = null;
    this._undoListForDataRef = null;

    this._redoList = [];

    this._notifyTraceService();
  }

  private _notifyTraceService() {
    //we don't add test this._traceService.isInEditingMode(), because the undo list is not reset when the user e.g. switches to view mode and again to editing mode
    this._traceService.canUndoOrRedoEditing = this.canUndoLastChange() || this.canRedoLastChange();
  }

  clearUndoList() {
    //this._closeDialog();
    this._undoList = [];
    this.canUndoLastChange.set(false);
    this._undoListForDataRef = null;

    //restart list with a dummy empty route
    //pushUndoList will also call this._notifyTraceService()
    this.pushUndoList({ type: TraceRouteDataUpdateType.COPY_TO_OTHER_TAB, data: TraceRouteData.getEmptyTraceRouteDataObject(null) }, false);
  }

  private _clearRedoList() {
    this._redoList = [];
    this.canRedoLastChange.set(false);

    this._notifyTraceService();
  }

  isSameDataRefAsUndoList(dataRef: string): boolean {
    return this._undoListForDataRef && dataRef && this._undoListForDataRef === dataRef;
  }

  isDataRefSetForUndoList(): boolean {
    return typeof this._undoListForDataRef === 'string';
  }

  /*
   * updating this value doesn't clear the undo list, you need to do that seperately
   *
   * @param newDataRef
   * @returns 
   */
  updateDataRefForUndoList(newDataRef: string): boolean {
    if (typeof newDataRef === 'string' && newDataRef.length > 0) {
      this._undoListForDataRef = newDataRef;
      return true;
    }
    return false;
  }

  pushUndoList(undoListItem: UndoListItemType, wasRedoAction: boolean) {
    //this._closeDialog();

    if (TraceRouteDataUpdateType.COPY_TO_OTHER_TAB === undoListItem.type && this._undoList.length === 1) {
      //this one item in the list was possibly added during call to method this.clearUndoList
      this._undoList = [];
    }

    if (TraceRouteDataUpdateType.COPY_TO_OTHER_TAB === undoListItem.type && this._undoList.length > 0) {
      this._notifyTraceService();
      return;
    }

    if (this._undoList.length === this._maxSize) {
      this._undoList.splice(0, 1);
    }

    this._undoList.push(undoListItem)
    //the first item doesn't count, it's added on startup
    this.canUndoLastChange.set(this._undoList.length > 1);

    if (!wasRedoAction) {
      //this will also call this._notifyTraceService()
      this._clearRedoList();
    } else {
      this._notifyTraceService();
    }
  }

  private _pushRedoList(undoListItem: UndoListItemType) {
    if (this._redoList.length === this._maxSize) {
      this._redoList.splice(0, 1);
    }

    this._redoList.push(undoListItem);
    this.canRedoLastChange.set(true);

    this._notifyTraceService();
  }

  popUndoList() {
    //this._closeDialog();

    if (this.canUndoLastChange()) {
      const lastUndoItem = this._undoList.pop();
      this._pushRedoList(lastUndoItem);
      this.canUndoLastChange.set(this._undoList.length > 1);

      this._notifyTraceService();
    }
  }

  private _popRedoList() {
    //this._closeDialog();

    if (this.canRedoLastChange()) {
      this._redoList.pop();
      this.canRedoLastChange.set(this._redoList.length > 0);

      this._notifyTraceService();
    }
  }

  getTopItemUndoList(): UndoListItemType {
    return this._undoList.length > 0 ? this._undoList[this._undoList.length - 1] : { type: null, data: null };
  }

  getTopItemRedoList(): UndoListItemType {
    return this._redoList.length > 0 ? this._redoList[this._redoList.length - 1] : { type: null, data: null };
  }

  undoLastChange() {
    //this._closeDialog();

    if (!this.canUndoLastChange()) {
      return;
    }

    if (this._traceService.isBusyWithAutoRouting()) {
      this._alertService.info(this._translateService.instant('trace.create-route.auto-routing-busy'), { 'autoClose': true });
      return;
    }

    /*
    const lastItem = this._undoList[this._undoList.length - 1];
    let lastTypeText = this._translateService.instant('trace.create-route.undotext_' + lastItem.type);
    lastTypeText = lastTypeText === ('trace.create-route.undotext_' + lastItem.type) ? null : lastTypeText;

    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.create-route.tool-title-undo'),
        text: this._translateService.instant('trace.create-route.confirmundo') + (lastTypeText ? '	&lsquo;' + lastTypeText + '&rsquo;?' : '')
      },
    });
    this.backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});
    */

    // this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
    //   if (result && result.data) {
    this.popUndoList();
    const lastUndoData = this._undoList[this._undoList.length - 1];
    //no broadcast
    this._traceService.setTraceRouteData_metadata(lastUndoData.data.metadata, true, false);
    //no broadcast
    this._traceService.setTraceRouteData_routes(lastUndoData.data.routes, TraceRouteDataUpdateType.UNDO, false);
    //broadcast change
    this._traceService.setTraceRouteData_wayPoints(lastUndoData.data.wayPoints, TraceRouteDataUpdateType.UNDO);

    this._traceService.removeWayPointHighlight();
    this._traceService.removeRoutePointHighlight();
    //  }
    //  this._confirmationDialog = null;
    //});
  }

  redoLastChange() {
    //this._closeDialog();

    if (!this.canRedoLastChange()) {
      return;
    }

    if (this._traceService.isBusyWithAutoRouting()) {
      //alert('busy');
      this._alertService.info(this._translateService.instant('trace.create-route.auto-routing-busy'), { 'autoClose': true });
      return;
    }

    /*
    const lastItem = this._redoList[this._redoList.length - 1];
    let lastTypeText = this._translateService.instant('trace.create-route.undotext_' + lastItem.type);
    lastTypeText = lastTypeText === ('trace.create-route.undotext_' + lastItem.type) ? null : lastTypeText;

    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.create-route.tool-title-redo'),
        text: this._translateService.instant('trace.create-route.confirmredo') + (lastTypeText ? '	&lsquo;' + lastTypeText + '&rsquo;?' : '')
      },
    });
    this.backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});
    */

    //this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
    //  if (result && result.data) {
    const lastRedoData = this._redoList[this._redoList.length - 1];
    this._popRedoList();
    //no broadcast
    this._traceService.setTraceRouteData_metadata(lastRedoData.data.metadata, false, false);
    //no broadcast
    this._traceService.setTraceRouteData_routes(lastRedoData.data.routes, TraceRouteDataUpdateType.REDO, false);
    //broadcast change
    this._traceService.setTraceRouteData_wayPoints(lastRedoData.data.wayPoints, TraceRouteDataUpdateType.REDO);

    this._traceService.removeWayPointHighlight();
    this._traceService.removeRoutePointHighlight();
    // }
    //  this._confirmationDialog = null;
    //});
  }

  private _closeDialog() {
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }
  }
}
